import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Check, CheckSquare, Flag, Mail, MessageSquare, Phone, RotateCcw, Send, StickyNote, Trash2, Trophy,
  UserPlus, X, ArrowRight, CalendarDays, Link2,
} from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { QUELLEN } from "@/components/vertrieb/Kontakte"
import { KontaktWahl } from "@/components/vertrieb/Kontakte"
import {
  type Aktivitaet, type Aufgabe, type Board, type Deal, type Kontakt,
  crmFetch, heuteIso, initialen, personFarbe, plusTage, tageSeit, tagKurz, vorWann,
} from "@/lib/crm"

/**
 * Seitenblatt eines Deals: Stufe, nächster Schritt, Notiz/Anruf/Mail/Aufgabe,
 * Details und Verlauf — alles auf einer Spalte, das Wichtigste oben.
 *
 * 🔴 Mails gehen an echte Interessenten. Senden fragt deshalb einmal nach
 * („Wirklich an … senden?"), und das Backend lässt auf dev nur Testadressen zu.
 */

type Detail = { deal: Deal; verlauf: Aktivitaet[]; aufgaben: Aufgabe[] }

const ABSENDER_SPEICHER = "vertrieb.absender"

export function DealBlatt({ dealId, board, zu, oeffneKontakt }: {
  dealId: number; board: Board; zu: () => void; oeffneKontakt?: (id: number) => void
}) {
  const qc = useQueryClient()
  const detailQ = useQuery<Detail>({
    queryKey: ["crm-deal", dealId],
    queryFn: () => crmFetch<Detail>(`/deals/${dealId}`),
  })

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      const t = e.target as HTMLElement
      if (t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) { t.blur(); return }
      zu()
    }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [zu])

  const aktualisiere = () => {
    qc.invalidateQueries({ queryKey: ["crm-deal", dealId] })
    qc.invalidateQueries({ queryKey: ["crm-board"] })
    qc.invalidateQueries({ queryKey: ["crm-faellig"] })
  }

  const speichern = useMutation({
    mutationFn: (daten: Partial<Deal>) => crmFetch<Deal>(`/deals/${dealId}`, { method: "PATCH", json: daten }),
    onSuccess: aktualisiere,
    onError: (e: Error) => toast(e.message, "error"),
  })
  const loeschen = useMutation({
    mutationFn: () => crmFetch(`/deals/${dealId}`, { method: "DELETE" }),
    onSuccess: () => { aktualisiere(); toast("Deal gelöscht"); zu() },
    onError: (e: Error) => toast(e.message, "error"),
  })

  const d = detailQ.data?.deal
  const stufen = board.stufen
  const stufe = stufen.find((s) => s.schluessel === d?.stufe)
  const offeneStufen = stufen.filter((s) => s.art === "offen")
  const gewonnen = stufen.find((s) => s.art === "gewonnen")
  const verloren = stufen.find((s) => s.art === "verloren")
  const [verlustOffen, setVerlustOffen] = useState(false)
  const [grund, setGrund] = useState("")

  // Index für die Fortschrittsleiste: Endstufen füllen alles.
  const fortschritt = useMemo(() => {
    if (!stufe) return -1
    if (stufe.art !== "offen") return offeneStufen.length
    return offeneStufen.findIndex((s) => s.schluessel === stufe.schluessel)
  }, [stufe, offeneStufen])

  return createPortal(
    <div className="vt-portal">
      <div className="vt-schleier" onClick={zu} />
      <aside className="vt-blatt" role="dialog" aria-label="Deal">
        {!d ? (
          <div className="vt-blatt-inhalt"><span className="vt-hinweis">{detailQ.error ? (detailQ.error as Error).message : "Lädt …"}</span></div>
        ) : (
          <>
            <div className="vt-blatt-kopf">
              <div className="vt-blatt-zeile">
                <div className="vt-blatt-titel">
                  <FeldText key={`t${d.id}`} className="vt-feld-titel" wert={d.titel} aria="Firma"
                    speichere={(v) => v.trim() && speichern.mutate({ titel: v })} />
                  <div className="vt-blatt-unter">
                    {d.ansprechpartner && (d.kontakt_id && oeffneKontakt
                      ? <button className="vt-link" title="Kontakt öffnen" onClick={() => oeffneKontakt(d.kontakt_id!)}>{d.ansprechpartner}</button>
                      : <span>{d.ansprechpartner}</span>)}
                    {d.telefon && <a href={`tel:${d.telefon.replace(/[^\d+]/g, "")}`}><Phone /> {d.telefon}</a>}
                    {d.email && <a href={`mailto:${d.email}`}><Mail /> {d.email}</a>}
                  </div>
                </div>
                {d.zustaendig && (
                  <span className="vt-avatar gross" style={{ background: personFarbe(d.zustaendig) }} title={`Zuständig: ${d.zustaendig}`}>
                    {initialen(d.zustaendig)}
                  </span>
                )}
                <button className="vt-ikon" onClick={zu} title="Schließen (Esc)"><X /></button>
              </div>

              <div className="vt-stufen" role="group" aria-label="Stufe">
                {offeneStufen.map((s, i) => (
                  <button key={s.schluessel} title={s.label}
                    style={{ background: i <= fortschritt ? (stufe?.art === "verloren" ? "#cbd5e1" : stufe?.art === "gewonnen" ? "var(--vt-gruen)" : (stufe?.farbe || "var(--accent-color)")) : undefined }}
                    onClick={() => speichern.mutate({ stufe: s.schluessel })} />
                ))}
              </div>
              <div className="vt-stufen-label">
                <span><b>{stufe?.label || d.stufe}</b> · seit {tageSeit(d.stufe_seit) === 0 ? "heute" : `${tageSeit(d.stufe_seit)} Tagen`}</span>
                <span>angelegt {new Date(d.created_at).toLocaleDateString("de-DE")}</span>
              </div>

              <div className="vt-blatt-knoepfe">
                {stufe?.art === "offen" ? (
                  <>
                    {(() => {
                      const naechste = offeneStufen[fortschritt + 1]
                      return naechste ? (
                        <button className="vt-knopf haupt klein" onClick={() => speichern.mutate({ stufe: naechste.schluessel })}>
                          {naechste.label} <ArrowRight />
                        </button>
                      ) : null
                    })()}
                    {gewonnen && (
                      <button className="vt-knopf gruen klein" onClick={() => { speichern.mutate({ stufe: gewonnen.schluessel }); toast("Gewonnen – stark! 🎉") }}>
                        <Trophy /> Gewonnen
                      </button>
                    )}
                    {verloren && (
                      <button className="vt-knopf rot klein" onClick={() => setVerlustOffen(true)}><Flag /> Verloren</button>
                    )}
                  </>
                ) : (
                  <button className="vt-knopf leise klein" onClick={() => speichern.mutate({ stufe: offeneStufen[offeneStufen.length - 1]?.schluessel || "lead" })}>
                    <RotateCcw /> Wieder öffnen
                  </button>
                )}
              </div>
              {verlustOffen && (
                <form className="vt-schritt" onSubmit={(e) => {
                  e.preventDefault()
                  if (!verloren) return
                  speichern.mutate({ stufe: verloren.schluessel, verlust_grund: grund || null })
                  setVerlustOffen(false); setGrund("")
                }}>
                  <input className="vt-eingabe" type="text" autoFocus value={grund} onChange={(e) => setGrund(e.target.value)}
                    placeholder="Woran lag's? (z. B. zu teuer, kein Bedarf, Wettbewerb)" />
                  <button className="vt-knopf rot klein" type="submit">Als verloren markieren</button>
                  <button className="vt-ikon" type="button" onClick={() => setVerlustOffen(false)}><X /></button>
                </form>
              )}
            </div>

            <div className="vt-blatt-inhalt">
              {stufe?.art === "verloren" && d.verlust_grund && (
                <div className="vt-warnung">Verloren: {d.verlust_grund}</div>
              )}
              {stufe?.art === "gewonnen" && <KundeVerknuepfen deal={d} speichere={(slug) => speichern.mutate({ kunde_slug: slug })} />}

              {stufe?.art === "offen" && (
                <NaechsterSchritt deal={d} speichere={(daten) => speichern.mutate(daten)} />
              )}

              <Verfassen deal={d} board={board} fertig={aktualisiere} />

              <Aufgaben dealId={d.id} aufgaben={detailQ.data!.aufgaben} fertig={aktualisiere} />

              <Details deal={d} speichere={(daten) => speichern.mutate(daten)} />

              <section className="vt-block">
                <div className="vt-block-kopf"><h4>Verlauf</h4></div>
                <div className="vt-block-rumpf">
                  <Verlauf eintraege={detailQ.data!.verlauf} stufen={board.stufen} />
                </div>
              </section>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="vt-knopf leise klein" onClick={() => {
                  if (window.confirm(`„${d.titel}" wirklich löschen?`)) loeschen.mutate()
                }}><Trash2 /> Deal löschen</button>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  )
}

// ── Bausteine ────────────────────────────────────────────────────────────

/** Eingabe, die beim Verlassen speichert — kein Speichern-Knopf nötig. */
export function FeldText({ wert, speichere, className, aria, typ = "text", platzhalter }: {
  wert: string | number | null; speichere: (v: string) => void; className?: string; aria?: string
  typ?: string; platzhalter?: string
}) {
  const [v, setV] = useState(wert == null ? "" : String(wert))
  useEffect(() => { setV(wert == null ? "" : String(wert)) }, [wert])
  return (
    <input className={className} aria-label={aria} type={typ} value={v} placeholder={platzhalter}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== (wert == null ? "" : String(wert))) speichere(v) }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur() }} />
  )
}

function NaechsterSchritt({ deal, speichere }: { deal: Deal; speichere: (d: Partial<Deal>) => void }) {
  const fehlt = !deal.naechster_schritt_am && !deal.offene_aufgaben
  const heute = heuteIso()
  const vorschlaege: [string, string][] = [["Heute", heute], ["Morgen", plusTage(heute, 1)], ["In 3 Tagen", plusTage(heute, 3)], ["Nächste Woche", plusTage(heute, 7)]]
  return (
    <section className={`vt-block ${fehlt ? "hervor" : ""}`}>
      <div className="vt-block-kopf">
        <h4>Nächster Schritt</h4>
        {fehlt && <span className="vt-pille gelb">fehlt</span>}
      </div>
      <div className="vt-block-rumpf" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="vt-schritt">
          <FeldText className="vt-eingabe" wert={deal.naechster_schritt} platzhalter="z. B. Strategie-Call vereinbaren"
            speichere={(v) => speichere({ naechster_schritt: v || null })} />
          <input className="vt-eingabe" type="date" value={deal.naechster_schritt_am || ""}
            onChange={(e) => speichere({ naechster_schritt_am: e.target.value || null })} />
        </div>
        <div className="vt-vorlagen">
          {vorschlaege.map(([l, t]) => (
            <button key={l} type="button" onClick={() => speichere({ naechster_schritt_am: t })}>{l}</button>
          ))}
          {deal.naechster_schritt_am && (
            <button type="button" onClick={() => speichere({ naechster_schritt: null, naechster_schritt_am: null })}>Erledigt ✓</button>
          )}
        </div>
      </div>
    </section>
  )
}

type Modus = "notiz" | "anruf" | "mail" | "aufgabe"

function Verfassen({ deal, board, fertig }: { deal: Deal; board: Board; fertig: () => void }) {
  const [modus, setModus] = useState<Modus>("notiz")
  return (
    <section className="vt-block">
      <div className="vt-reiter" role="tablist">
        {([
          ["notiz", "Notiz", StickyNote], ["anruf", "Anruf", Phone], ["mail", "E-Mail", Mail], ["aufgabe", "Aufgabe", CheckSquare],
        ] as const).map(([k, l, Ikon]) => (
          <button key={k} role="tab" aria-selected={modus === k} className={modus === k ? "an" : ""} onClick={() => setModus(k)}>
            <Ikon /> {l}
          </button>
        ))}
      </div>
      {modus === "mail"
        ? <MailVerfassen deal={deal} board={board} fertig={fertig} />
        : modus === "aufgabe"
          ? <AufgabeVerfassen deal={deal} fertig={fertig} />
          : <NotizVerfassen key={modus} deal={deal} art={modus} fertig={fertig} />}
    </section>
  )
}

function NotizVerfassen({ deal, art, fertig }: { deal: Deal; art: "notiz" | "anruf"; fertig: () => void }) {
  const [text, setText] = useState("")
  const m = useMutation({
    mutationFn: () => crmFetch(`/deals/${deal.id}/aktivitaeten`, { method: "POST", json: { art, text } }),
    onSuccess: () => { setText(""); fertig(); toast(art === "anruf" ? "Anruf festgehalten" : "Notiz gespeichert") },
    onError: (e: Error) => toast(e.message, "error"),
  })
  return (
    <form className="vt-verfassen" onSubmit={(e) => { e.preventDefault(); if (text.trim()) m.mutate() }}>
      <textarea className="vt-eingabe" value={text} onChange={(e) => setText(e.target.value)}
        placeholder={art === "anruf" ? "Wie lief das Gespräch? Was wurde vereinbart?" : "Notiz zum Deal …"}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) m.mutate() }} />
      <div className="vt-verfassen-fuss">
        <span className="vt-hinweis">⌘ + Enter speichert</span>
        <button className="vt-knopf haupt klein" disabled={!text.trim() || m.isPending}>
          {art === "anruf" ? <><Phone /> Anruf festhalten</> : <><MessageSquare /> Speichern</>}
        </button>
      </div>
    </form>
  )
}

function lies(k: string) { try { return localStorage.getItem(k) || "" } catch { return "" } }

function MailVerfassen({ deal, board, fertig }: { deal: Deal; board: Board; fertig: () => void }) {
  const [an, setAn] = useState(deal.email || "")
  const [absender, setAbsender] = useState(lies(ABSENDER_SPEICHER) || "")
  const [betreff, setBetreff] = useState("")
  const [text, setText] = useState("")
  const [fragen, setFragen] = useState(false)
  useEffect(() => { setAn(deal.email || "") }, [deal.email])

  const fuelle = (t: string) => {
    const name = (deal.ansprechpartner || "").trim()
    const werte: Record<string, string> = {
      vorname: name.split(" ")[0] || "", name, firma: deal.titel, absender: absender.trim(),
    }
    return t.replace(/\{(\w+)\}/g, (m, k) => (k in werte ? werte[k] : m))
  }

  const senden = useMutation({
    mutationFn: () => crmFetch(`/deals/${deal.id}/mail`, {
      method: "POST", json: { an, betreff, text, absender_name: absender ? `${absender} | werkXpartner` : "werkXpartner" },
    }),
    onSuccess: () => {
      setBetreff(""); setText(""); setFragen(false); fertig()
      toast(`Mail an ${an} gesendet ✓`)
    },
    onError: (e: Error) => { setFragen(false); toast(e.message, "error") },
  })

  const bereit = an.includes("@") && betreff.trim() && text.trim()
  return (
    <form className="vt-verfassen" onSubmit={(e) => { e.preventDefault(); if (bereit) setFragen(true) }}>
      {!board.mail_bereit && <div className="vt-warnung">Mailversand ist auf diesem Server nicht eingerichtet.</div>}
      <div className="vt-vorlagen" aria-label="Vorlagen">
        {board.vorlagen.map((v) => (
          <button key={v.schluessel} type="button" onClick={() => { setBetreff(v.betreff); setText(fuelle(v.text)) }}>{v.label}</button>
        ))}
      </div>
      <div className="vt-mail-zeile"><span>Von</span>
        <input className="vt-eingabe" value={absender} placeholder="Dein Name (z. B. Paul Lux)"
          onChange={(e) => { setAbsender(e.target.value); try { localStorage.setItem(ABSENDER_SPEICHER, e.target.value) } catch { /* egal */ } }} />
      </div>
      <div className="vt-mail-zeile"><span>An</span>
        <input className="vt-eingabe" type="email" value={an} onChange={(e) => setAn(e.target.value)} placeholder="name@firma.de" />
      </div>
      <div className="vt-mail-zeile"><span>Betreff</span>
        <input className="vt-eingabe" value={betreff} onChange={(e) => setBetreff(e.target.value)} />
      </div>
      <textarea className="vt-eingabe" style={{ minHeight: 180 }} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Nachricht …" />
      {fragen ? (
        <div className="vt-bestaetigen">
          <span>Wirklich an <b>{an}</b> senden?</span>
          <button type="button" className="vt-knopf leise klein" onClick={() => setFragen(false)}>Abbrechen</button>
          <button type="button" className="vt-knopf akzent klein" disabled={senden.isPending} onClick={() => senden.mutate()}>
            <Send /> {senden.isPending ? "Sendet …" : "Jetzt senden"}
          </button>
        </div>
      ) : (
        <div className="vt-verfassen-fuss">
          <span className="vt-hinweis">Geht von kontakt@werkxpartner.de raus, Antworten landen dort.</span>
          <button className="vt-knopf akzent klein" disabled={!bereit || !board.mail_bereit}><Send /> Senden …</button>
        </div>
      )}
    </form>
  )
}

function AufgabeVerfassen({ deal, fertig }: { deal: Deal; fertig: () => void }) {
  const heute = heuteIso()
  const [titel, setTitel] = useState("")
  const [tag, setTag] = useState(plusTage(heute, 1))
  const m = useMutation({
    mutationFn: () => crmFetch(`/deals/${deal.id}/aufgaben`, { method: "POST", json: { titel, faellig_am: tag || null } }),
    onSuccess: () => { setTitel(""); fertig(); toast("Aufgabe angelegt") },
    onError: (e: Error) => toast(e.message, "error"),
  })
  return (
    <form className="vt-verfassen" onSubmit={(e) => { e.preventDefault(); if (titel.trim()) m.mutate() }}>
      <div className="vt-schritt">
        <input className="vt-eingabe" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Was ist zu tun? (z. B. Angebot nachfassen)" />
        <input className="vt-eingabe" type="date" value={tag} onChange={(e) => setTag(e.target.value)} />
      </div>
      <div className="vt-verfassen-fuss">
        <div className="vt-vorlagen" style={{ marginRight: "auto" }}>
          {([["Morgen", 1], ["In 3 Tagen", 3], ["Nächste Woche", 7]] as const).map(([l, n]) => (
            <button key={l} type="button" onClick={() => setTag(plusTage(heute, n))}>{l}</button>
          ))}
        </div>
        <button className="vt-knopf haupt klein" disabled={!titel.trim() || m.isPending}><CalendarDays /> Aufgabe anlegen</button>
      </div>
    </form>
  )
}

function Aufgaben({ aufgaben, fertig }: { dealId: number; aufgaben: Aufgabe[]; fertig: () => void }) {
  const [erledigteZeigen, setErledigteZeigen] = useState(false)
  const m = useMutation({
    mutationFn: ({ id, daten }: { id: number; daten: Record<string, unknown> }) =>
      crmFetch(`/aufgaben/${id}`, { method: "PATCH", json: daten }),
    onSuccess: fertig,
    onError: (e: Error) => toast(e.message, "error"),
  })
  const weg = useMutation({
    mutationFn: (id: number) => crmFetch(`/aufgaben/${id}`, { method: "DELETE" }),
    onSuccess: fertig,
  })
  const offen = aufgaben.filter((a) => !a.erledigt_am)
  const erledigt = aufgaben.filter((a) => a.erledigt_am)
  if (!aufgaben.length) return null
  const heute = heuteIso()
  return (
    <section className="vt-block">
      <div className="vt-block-kopf">
        <h4>Aufgaben {offen.length ? `· ${offen.length} offen` : ""}</h4>
        {erledigt.length > 0 && (
          <button className="vt-knopf leise klein" onClick={() => setErledigteZeigen(!erledigteZeigen)}>
            {erledigteZeigen ? "Erledigte ausblenden" : `${erledigt.length} erledigt`}
          </button>
        )}
      </div>
      <div className="vt-block-rumpf">
        <ul className="vt-aufgaben">
          {[...offen, ...(erledigteZeigen ? erledigt : [])].map((a) => {
            const f = a.faellig_am
            const art = !f || a.erledigt_am ? "" : f < heute ? "rot" : f === heute ? "gelb" : ""
            return (
              <li key={a.id} className={`vt-aufgabe ${a.erledigt_am ? "erledigt" : ""}`}>
                <button className={`vt-haken ${a.erledigt_am ? "an" : ""}`} title={a.erledigt_am ? "Wieder öffnen" : "Erledigt"}
                  onClick={() => m.mutate({ id: a.id, daten: { erledigt: !a.erledigt_am } })}><Check /></button>
                <span className="vt-aufgabe-text">{a.titel}</span>
                {f && <span className={`vt-pille ${art}`}>{tagKurz(f)}</span>}
                <button className="vt-ikon" title="Löschen" onClick={() => weg.mutate(a.id)}><Trash2 /></button>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

function Details({ deal, speichere }: { deal: Deal; speichere: (d: Partial<Deal>) => void }) {
  const zust = useQuery<{ items: string[] }>({
    queryKey: ["portal-aufgaben-zustaendige"],
    queryFn: async () => (await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" })).json(),
    staleTime: 600_000,
  })
  const zahl = (v: string) => (v.trim() ? Number(v.replace(/\./g, "").replace(",", ".")) : null)
  const leer = (v: string) => v.trim() || null
  return (
    <section className="vt-block">
      <div className="vt-block-kopf"><h4>Details</h4></div>
      <div className="vt-block-rumpf">
        <div className="vt-felder">
          <div className="vt-feld breit"><label>Kontakt</label>
            <KontaktAmDeal deal={deal} speichere={speichere} /></div>
          <div className="vt-feld"><label>Ansprechperson</label>
            <FeldText wert={deal.ansprechpartner} speichere={(v) => speichere({ ansprechpartner: leer(v) })} /></div>
          <div className="vt-feld"><label>Telefon</label>
            <FeldText typ="tel" wert={deal.telefon} speichere={(v) => speichere({ telefon: leer(v) })} /></div>
          <div className="vt-feld breit"><label>E-Mail</label>
            <FeldText typ="email" wert={deal.email} speichere={(v) => speichere({ email: leer(v) })} /></div>
          <div className="vt-feld"><label>Wert pro Monat (€)</label>
            <FeldText wert={deal.wert_monat} speichere={(v) => speichere({ wert_monat: zahl(v) })} /></div>
          <div className="vt-feld"><label>Einmalig (€)</label>
            <FeldText wert={deal.wert_einmalig} speichere={(v) => speichere({ wert_einmalig: zahl(v) })} /></div>
          <div className="vt-feld"><label>Zuständig</label>
            <select value={deal.zustaendig || ""} onChange={(e) => speichere({ zustaendig: e.target.value || null })}>
              <option value="">niemand</option>
              {Array.from(new Set([deal.zustaendig, ...(zust.data?.items || [])].filter(Boolean) as string[])).map((n) => <option key={n}>{n}</option>)}
            </select></div>
          <div className="vt-feld"><label>Erwarteter Abschluss</label>
            <input type="date" value={deal.abschluss_am || ""} onChange={(e) => speichere({ abschluss_am: e.target.value || null })} /></div>
          <div className="vt-feld"><label>Quelle</label>
            <select value={deal.quelle || ""} onChange={(e) => speichere({ quelle: e.target.value || null })}>
              <option value="">–</option>
              {Array.from(new Set([deal.quelle, ...QUELLEN].filter(Boolean) as string[])).map((q) => <option key={q}>{q}</option>)}
            </select></div>
          <div className="vt-feld breit"><label>Notiz</label>
            <NotizFeld wert={deal.notiz} speichere={(v) => speichere({ notiz: leer(v) })} /></div>
        </div>
      </div>
    </section>
  )
}

/** Kontakt wechseln/entfernen. Ohne Kontakt bleiben Name/Mail/Telefon am Deal. */
function KontaktAmDeal({ deal, speichere }: { deal: Deal; speichere: (d: Partial<Deal>) => void }) {
  const gewaehlt = deal.kontakt_id
    ? ({ id: deal.kontakt_id, name: deal.ansprechpartner, firma: deal.kontakt_firma ?? null, email: deal.email } as Kontakt)
    : null
  return <KontaktWahl gewaehlt={gewaehlt} waehle={(k) => speichere({ kontakt_id: k ? k.id : null })} />
}

export function NotizFeld({ wert, speichere }: { wert: string | null; speichere: (v: string) => void }) {
  const [v, setV] = useState(wert || "")
  useEffect(() => { setV(wert || "") }, [wert])
  return <textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== (wert || "")) speichere(v) }}
    placeholder="Was man über den Betrieb wissen sollte …" />
}

function KundeVerknuepfen({ deal, speichere }: { deal: Deal; speichere: (slug: string | null) => void }) {
  const kunden = useQuery<{ items: { slug: string; name: string }[] }>({
    queryKey: ["kunden-liste"],
    queryFn: async () => (await fetch("/api/v1/kunden", { credentials: "include" })).json(),
    staleTime: 600_000,
  })
  const name = kunden.data?.items.find((k) => k.slug === deal.kunde_slug)?.name
  return (
    <section className="vt-block" style={{ background: "var(--vt-gruen-bg)", boxShadow: "none" }}>
      <div className="vt-block-rumpf" style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 16 }}>
        <Trophy size={20} color="#0b7a6d" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 650, color: "#0b5f55" }}>Gewonnen{deal.kunde_slug ? ` · Kunde ${name || deal.kunde_slug}` : ""}</div>
          <div className="vt-hinweis">{deal.kunde_slug ? "Mit dem Kunden im Portal verknüpft." : "Mit einem Kunden im Portal verknüpfen, damit die Betreuung weiß, woher er kommt."}</div>
        </div>
        {deal.kunde_slug ? (
          <a className="vt-knopf leise klein" href={`/kunde/${deal.kunde_slug}`}><Link2 /> Öffnen</a>
        ) : (
          <select className="vt-eingabe" style={{ width: 200 }} value="" onChange={(e) => e.target.value && speichere(e.target.value)}>
            <option value="">Kunde wählen …</option>
            {(kunden.data?.items || []).map((k) => <option key={k.slug} value={k.slug}>{k.name}</option>)}
          </select>
        )}
        {!deal.kunde_slug && (
          <a className="vt-knopf leise klein" href="/kunden" title="Neuen Kunden unter Kunden anlegen"><UserPlus /></a>
        )}
      </div>
    </section>
  )
}

const ART_IKON = { notiz: StickyNote, anruf: Phone, mail: Mail, termin: CalendarDays, stufe: ArrowRight, angelegt: Flag, aufgabe: Check }

export function Verlauf({ eintraege, stufen }: { eintraege: Aktivitaet[]; stufen: Board["stufen"] }) {
  const label = (k?: string) => stufen.find((s) => s.schluessel === k)?.label || k || ""
  if (!eintraege.length) return <span className="vt-hinweis">Noch nichts passiert.</span>
  return (
    <ul className="vt-verlauf">
      {eintraege.map((e) => {
        const Ikon = ART_IKON[e.art] || StickyNote
        const wer = e.von ? <b>{e.von}</b> : <b>Jemand</b>
        let kopf: React.ReactNode
        switch (e.art) {
          case "mail": kopf = <>{wer} hat eine Mail an {e.meta?.an} gesendet</>; break
          case "anruf": kopf = <>{wer} hat telefoniert</>; break
          case "stufe": kopf = <>{wer} hat verschoben: {label(e.meta?.von)} → <b>{label(e.meta?.nach)}</b></>; break
          case "angelegt": kopf = <>{wer} hat den Deal angelegt</>; break
          case "aufgabe": kopf = <>{wer}: {e.text}</>; break
          default: kopf = <>{wer} hat eine Notiz geschrieben</>
        }
        return (
          <li key={e.id} className="vt-ereignis">
            <span className={`vt-ereignis-ikon ${e.art}`}><Ikon /></span>
            <div className="vt-ereignis-inhalt">
              <div className="vt-ereignis-kopf">{kopf}{e.deal_titel ? <> · <b>{e.deal_titel}</b></> : null} · <span title={new Date(e.created_at).toLocaleString("de-DE")}>{vorWann(e.created_at)}</span></div>
              {e.text && !["stufe", "angelegt", "aufgabe"].includes(e.art) && (
                <div className={`vt-ereignis-text ${e.art === "mail" ? "mail" : ""}`}>
                  {e.art === "mail" && e.meta?.betreff && <div className="vt-ereignis-betreff">{e.meta.betreff}</div>}
                  {e.text}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
